/*
Copyright 2026 Integrated Tech9labs Pvt. Ltd.
All Rights Reserved.

This file is part of Swift and is licensed under the GNU Affero General
Public License, version 3 (see the LICENSE file at the root of the
repository).
*/

/**
 * A client's DR settings (decision 2026-10-05): Swift manages many client environments, one project each, and
 * every client sets DR its own way. These are the defaults every plan of the client inherits: addresses
 * (dhcp, keep, static, translate), security (DR defaults, existing groups, or rules Swift turns into a group),
 * networks, DNS, health checks, approvals and timeouts. A plan overrides any of them; validation says where each
 * setting came from.
 */

import * as React from "react";
import styled from "styled-components";
import { Save } from "lucide-react";

import { drErrorMessage } from "@src/components/modules/DrModule/drUtils";
import { ActionButton } from "@src/components/ui/DetailsKit";
import { Muted, formatDateTime, timeAgo } from "@src/components/ui/Lists/ListTable";
import { FieldHint, FormField, FormGrid, Notice, OpsInput, OpsTextarea } from "@src/components/ui/OpsDialog";
import drSource from "@src/sources/DrSource";

import type { DrClientSettings } from "@src/@types/Dr";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  max-width: 880px;
`;
const Mono = styled(OpsTextarea)`
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  min-height: 300px;
`;

/** A starting point: one client's typical choices, to edit. */
export const EXAMPLE_SETTINGS = {
  ip: {
    mode: "translate",
    translations: [{ from: "10.1.0.0/16", to: "10.201.0.0/16" }],
    unmatched: "fail",
    test: { mode: "dhcp" },
  },
  security: {
    mode: "rules",
    rules: [
      { direction: "inbound", protocol: "tcp", ports: "443", from: "10.0.0.0/8" },
      { direction: "inbound", protocol: "tcp", ports: "22", from: "10.250.0.0/24" },
    ],
  },
  health_checks: [],
};

const DrClientSettingsView = () => {
  const [current, setCurrent] = React.useState<DrClientSettings | null>(null);
  const [text, setText] = React.useState("");
  const [reason, setReason] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const found = await drSource.clientSettings();
      setCurrent(found);
      setText(JSON.stringify(found.settings || {}, null, 2));
    } catch (err) {
      setError(drErrorMessage(err, "Could not load the client's DR settings."));
    }
  }, []);
  React.useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    let settings: { [section: string]: any };
    try {
      settings = text.trim() ? JSON.parse(text) : {};
    } catch (err: any) {
      setError(`The settings are not valid JSON: ${err.message}`);
      return;
    }
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await drSource.updateClientSettings(settings, reason.trim() || undefined);
      setCurrent(updated);
      setText(JSON.stringify(updated.settings || {}, null, 2));
      setReason("");
      setSaved(true);
    } catch (err) {
      setError(drErrorMessage(err, "SwiftIQ refused these settings."));
    } finally {
      setBusy(false);
    }
  };

  if (!current) return error ? <Notice tone="danger">{error}</Notice> : <Muted>Loading…</Muted>;
  return (
    <Wrapper data-test-id="dr-client-settings">
      <Notice tone="neutral" title="Defaults for every plan of this client">
        A plan uses these settings for what it does not set itself, section by section and key by key. Validation
        shows where each setting came from. Runs keep the settings they started with.
      </Notice>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {saved ? (
        <Notice tone="success" title="Saved">
          Version {current.version}. Plans pick the settings up at their next validation or run.
        </Notice>
      ) : null}
      <Muted data-test-id="dr-client-settings-version">
        {current.version
          ? <>Version {current.version}{current.updated_by ? ` by ${current.updated_by}` : ""}
            {current.updated_at ? <> · <span title={formatDateTime(current.updated_at)}>{timeAgo(current.updated_at)}</span></> : null}</>
          : "Not set: plans use SwiftIQ's defaults (addresses by DHCP, the DR network's security, no DNS)."}
      </Muted>
      <FormGrid>
        <FormField label="Settings (JSON)" span={2} as="div">
          <Mono value={text} onChange={e => setText(e.target.value)} spellCheck={false} data-test-id="dr-client-settings-json" />
          <FieldHint>
            Sections: {current.sections.join(", ")}. Addresses: <code>ip.mode</code> dhcp, keep, static or translate
            (<code>translations</code> from one network to another, keeping the host part). Security:{" "}
            <code>security.mode</code> dr_defaults, groups (existing groups, per DR network or VM) or rules (SwiftIQ
            creates a group per VM and removes it with the VM). <code>workloads</code> entries override one VM;{" "}
            <code>test</code> entries apply to test failovers, which use DHCP unless set.{" "}
            <a
              href="#example"
              onClick={e => {
                e.preventDefault();
                setText(JSON.stringify(EXAMPLE_SETTINGS, null, 2));
              }}
              data-test-id="dr-client-settings-example"
            >
              Start from an example
            </a>
          </FieldHint>
        </FormField>
        <FormField label="Why this change" span={2}>
          <OpsInput
            value={reason}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReason(e.target.value)}
            placeholder="Kept in the audit log"
            data-test-id="dr-client-settings-reason"
          />
        </FormField>
      </FormGrid>
      <div>
        <ActionButton type="button" onClick={save} disabled={busy} data-test-id="dr-client-settings-save">
          <Save size={16} /> {busy ? "Saving…" : "Save settings"}
        </ActionButton>
      </div>
    </Wrapper>
  );
};

export default DrClientSettingsView;
